/**
 * Dismissal bookkeeping for the "app is still in development" banner.
 *
 * The banner is a nag, not a one-time notice: closing it hides it for three
 * days and then it comes back, because the app is still unvalidated and someone
 * who dismissed it last week should be reminded before they lean on it. It stops
 * for good only when the app leaves `inDevelopment` status, which is decided by
 * the app's status, not by anything stored here.
 *
 * Dismissal is per app and per browser - localStorage, like the welcome popup in
 * Apps.tsx. Nothing about it is worth a round trip to the server.
 */

export const DISMISSAL_WINDOW_MS = 3 * 24 * 60 * 60 * 1000;

export const dismissalStorageKey = (appToken: string): string =>
  `mashlom-in-development-banner:${appToken}`;

/**
 * Whether a stored dismissal still suppresses the banner.
 *
 * Anything unreadable - absent, non-numeric, or stamped in the future because
 * the device clock moved - counts as "not dismissed". Showing the banner one
 * extra time is the harmless failure; silently swallowing it is not.
 *
 * Exported for tests.
 */
export const isDismissalActive = (
  storedValue: string | null | undefined,
  now: number
): boolean => {
  if (!storedValue) return false;
  const dismissedAt = Number(storedValue);
  if (!Number.isFinite(dismissedAt)) return false;
  if (dismissedAt > now) return false;
  return now - dismissedAt < DISMISSAL_WINDOW_MS;
};

export const isBannerDismissed = (appToken: string, now: number = Date.now()): boolean => {
  try {
    return isDismissalActive(window.localStorage.getItem(dismissalStorageKey(appToken)), now);
  } catch {
    // Storage blocked (private mode, hardened browser). Treat as not dismissed.
    return false;
  }
};

export const rememberBannerDismissal = (appToken: string, now: number = Date.now()): void => {
  try {
    window.localStorage.setItem(dismissalStorageKey(appToken), String(now));
  } catch {
    // Storage blocked - the banner simply returns on the next visit.
  }
};
